import { CheckCircleOutlined } from "@ant-design/icons";
import { Button, Collapse, Grid } from "antd";
import { loQueEstaCargado } from "../../components/documentoCargado";
import { aportadoTecnico } from "../../../../utils/documentoAportado";

/**
 * El listado de documentos tecnicos de una postulacion.
 *
 * Mismo tratamiento que los administrativos: el documento ya puesto se
 * dibujaba como una capa verde en posicion absoluta encima de la tarjeta
 * normal, que en columna angosta dejaba el texto de las dos superpuesto.
 * Ahora se dibuja en un estado o en el otro, en flujo normal.
 *
 * En pantalla angosta cada documento es un desplegable, uno por fila; desde
 * md se vuelven tarjetas, de a dos por fila en pantalla ancha.
 *
 * Depende de mucho menos que su equivalente administrativo, que arrastra
 * cuatro modales: aqui bastan las cuatro funciones de cargar, borrar,
 * descargar y actualizar la lista.
 */
export interface AccionesDocumentosTecnicos {
  documentos: any[];
  setDocumentos: (valor: any) => void;
  guardarArchivosTecnicos: (...args: any[]) => any;
  eliminarArchivo: (...args: any[]) => any;
  descargarArchivo: (...args: any[]) => any;
  /** Abre el documento cargado en el visor, sin tener que bajarlo. */
  verDocumento: (archivo: string) => void;
  documentosUsuario: any[];
  setDocumentosUsuario: (valor: any) => void;
  /** El modal de los documentos que se entregan como escrito y video. */
  setCurrentIndex: (valor: any) => void;
  setEscrito: (valor: any) => void;
  setUrlVideo: (valor: any) => void;
  setModalEscritoVideo: (valor: boolean) => void;
}

interface IProps {
  acciones: AccionesDocumentosTecnicos;
}

const esEscritoYVideo = (data: any): boolean => data?.tipo_documento === "Escrito y Video";

/**
 * El tecnico esta puesto si tiene archivo. El escrito y video no lo tiene:
 * se diligencia en linea, y cuenta con el texto o con el enlace.
 */
export const estaCargadoTecnico = aportadoTecnico;

export const ListaDocumentosTecnicos = ({ acciones }: IProps) => {
  const {
    documentos,
    setDocumentos,
    guardarArchivosTecnicos,
    eliminarArchivo,
    descargarArchivo,
    verDocumento,
    documentosUsuario,
    setDocumentosUsuario,
    setCurrentIndex,
    setEscrito,
    setUrlVideo,
    setModalEscritoVideo,
  } = acciones;

  // En el primer render todavia no hay medida; se asume ancho, que es la
  // vista que no cambia de forma cuando la medida llega.
  const pantalla = Grid.useBreakpoint();
  const enMovil = pantalla.md === false;

  /** De a dos por fila desde md; mas abajo cada documento es un desplegable. */
  const claseColumna = "col-12 col-md-6 mt-3 mb-2 d-flex";

  const abrirEscritoYVideo = (data: any, index: number) => {
    setCurrentIndex(index);
    setEscrito(data.escrito || "");
    setUrlVideo(data.url_video || "");
    setModalEscritoVideo(true);
  };

  const quitarDocumento = (e: any, data: any, index: number) => {
    const filterDoc = documentosUsuario.filter(
      (documentoTecnico: any) => documentoTecnico.tipo_documento !== data.tipo_documento
    );
    setDocumentosUsuario(filterDoc);

    if (esEscritoYVideo(data)) {
      // No hay archivo que borrar: se vacia lo que se diligencio en linea.
      setDocumentos(
        documentos.map((doc: any, i: number) =>
          i === index ? { ...doc, escrito: "", url_video: "" } : doc
        )
      );
      return;
    }

    eliminarArchivo(e, index, data.tipo_documento);
  };

  /** Lo que se puede hacer con un documento que todavia no esta puesto. */
  const cuerpoPendiente = (data: any, index: number) => (
    <>
      <div className="font-family-Montserrat-Regular font-size-12px mb-2">{data.descripcion}</div>
      <div className="mb-2">
        {data.url_documento || data.url_participante ? (
          <Button
            className="button-yellow-ghost p-0"
            onClick={() => {
              descargarArchivo(data.url_documento || data.url_participante, data.descripcion);
            }}
          >
            DESCARGAR FORMATO
          </Button>
        ) : (
          <span className="font-size-12px" style={{ color: "#ff8900" }}>
            SIN FORMATO
          </span>
        )}
      </div>
      <div className="mt-auto">
        <Button
          className="button-gray-ghost p-0 m-0"
          onClick={() => {
            if (esEscritoYVideo(data)) {
              abrirEscritoYVideo(data, index);
              return;
            }
            document.getElementById(`input-tecnico-${index}`)?.click();
          }}
        >
          {esEscritoYVideo(data) ? "DILIGENCIAR" : "CARGAR DOCUMENTO"}
        </Button>
        <input
          id={`input-tecnico-${index}`}
          className="inputs-ref"
          type="file"
          hidden
          onChange={(e) => {
            guardarArchivosTecnicos(data, e, index, data.tipo_documento);
            e.target.value = "";
          }}
        />
      </div>
    </>
  );

  /**
   * Un documento ya puesto. Dice que quedo cargado: antes solo decia
   * "Cargado", asi que no habia forma de saber que archivo era sin borrarlo y
   * volver a subirlo.
   */
  const cuerpoCargado = (data: any, index: number) => (
    <>
      <div className="documento-archivo font-size-12px mb-2" title={loQueEstaCargado(data)}>
        {loQueEstaCargado(data)}
      </div>
      <div className="mt-auto d-flex flex-wrap align-items-center" style={{ gap: 24 }}>
        {/*
          Lo que se cargo se abre en el visor, que muestra el PDF sin bajarlo.
          El escrito y video no es un archivo: se abre su formulario.
        */}
        <Button
          className="button-blanco-ghost text-uppercase font-family-Montserrat-Regular p-0"
          onClick={() => {
            if (esEscritoYVideo(data)) {
              abrirEscritoYVideo(data, index);
              return;
            }
            verDocumento(data.url_participante);
          }}
        >
          Ver
        </Button>
        <Button
          className="button-blanco-ghost text-uppercase font-family-Montserrat-Regular p-0"
          onClick={(e) => quitarDocumento(e, data, index)}
        >
          Eliminar
        </Button>
      </div>
    </>
  );

  const cuerpo = (data: any, index: number) =>
    estaCargadoTecnico(data) ? cuerpoCargado(data, index) : cuerpoPendiente(data, index);

  const titulo = (data: any) => (
    <span className="font-family-Montserrat-Bold font-size-14px">
      {estaCargadoTecnico(data) && <CheckCircleOutlined className="me-2" />}
      {data.tipo_documento}
    </span>
  );

  if (enMovil) {
    return (
      <Collapse className="mt-4 mb-5 documentos-desplegables">
        {documentos.map((data: any, index: number) => (
          <Collapse.Panel
            key={String(data.id ?? index)}
            className={estaCargadoTecnico(data) ? "documento-cargado" : ""}
            header={titulo(data)}
          >
            <div className="d-flex flex-column">{cuerpo(data, index)}</div>
          </Collapse.Panel>
        ))}
      </Collapse>
    );
  }

  return (
    <div className="row" style={{ paddingTop: "16px", paddingBottom: "5%" }}>
      {documentos.map((data: any, index: number) => (
        <div className={claseColumna} key={data.id ?? index}>
          <div
            className={`cards-documentos d-flex flex-column ${
              estaCargadoTecnico(data) ? "cards-documentos-cargado" : ""
            }`}
          >
            <div className="mb-2">{titulo(data)}</div>
            {cuerpo(data, index)}
          </div>
        </div>
      ))}
    </div>
  );
};
